import { SyncStatusView } from './models'
import { User } from '../../../../../models/User'
import { ContactChange } from '../../../../../models/MeetingSupport'
import { getUserDisplayName } from '../../common/aifeatures/utils'
import { isDocumentConnectorServiceId } from '../../../../../common/integrations'

const SALESFORCE_RECORD_OBJECT_NAMES = ['Note', 'Event', 'Contact', 'Account', 'Case']

interface getExternalLinkParams {
  syncStatus: SyncStatusView
  currentUser: User | null
}

const objectNameMapper: { [key: string]: { [key: string]: string } } = {
  wealthbox: {
    notes: 'status_updates',
    task: 'tasks',
    contact: 'contacts',
  },
  redtail: {
    note: 'notes',
    task: 'activities',
  },
  salesforce: {
    task: 'Task',
    contact: 'Contact',
    account: 'Account',
  },
  practifi: {
    task: 'Task',
  },
  xlr8: {
    task: 'Task',
  },
  elements: {
    task: 'Task',
  },
  edge: {
    task: 'Task',
  },
}

const getCRMObjectName = (serviceId: string, objectName: string): string => {
  return objectNameMapper[serviceId]?.[objectName] ?? objectName
}

export const getExternalLink = ({ syncStatus, currentUser }: getExternalLinkParams): string => {
  const { integrations: currentUserIntegrations } = currentUser ?? {}

  const userIntegration = currentUserIntegrations?.find((i) => i.serviceId === syncStatus.serviceId)

  const crmObjectName = getCRMObjectName(syncStatus.serviceId, syncStatus.externalObjectName ?? 'note')

  if (isDocumentConnectorServiceId(syncStatus.serviceId)) {
    return syncStatus.location ?? ''
  }

  switch (syncStatus.serviceId) {
    case 'wealthbox':
      if (!userIntegration?.externalAccountId) {
        return ''
      }
      return `https://app.crmworkspace.com/${userIntegration?.externalAccountId}/${crmObjectName}/${syncStatus.externalId}`
    case 'salesforce':
    case 'practifi':
    case 'xlr8':
    case 'elements':
    case 'blackdiamond':
      if (!syncStatus.baseUrl) {
        return ''
      }
      if (SALESFORCE_RECORD_OBJECT_NAMES.includes(crmObjectName)) {
        return `${syncStatus.baseUrl}/lightning/r/${crmObjectName}/${syncStatus.externalId}/view`
      }
      return `${syncStatus.baseUrl}/lightning/o/${crmObjectName}/home?recordId=${syncStatus.externalId}`
    case 'redtail':
      switch (crmObjectName) {
        case 'activities':
          return `https://crm.redtailtechnology.com/activities/${syncStatus.externalId}`
        case 'contact':
          return `https://crm.redtailtechnology.com/contacts/${syncStatus.externalContactId}`
      }
      return `https://crm.redtailtechnology.com/contacts/${syncStatus.externalContactId}/${crmObjectName}`
    case 'hubspot':
      if (!userIntegration?.externalAccountId) {
        return ''
      }
      if (syncStatus.externalId === syncStatus.externalContactId) {
        return `https://app.hubspot.com/contacts/${userIntegration?.externalAccountId}/contact/${syncStatus.externalContactId}`
      }
      return `https://app.hubspot.com/contacts/${userIntegration?.externalAccountId}/contact/${syncStatus.externalContactId}/?engagement=${syncStatus.externalId}`
    case 'dynamics':
    case 'engage':
      return ''
    case 'emoney': {
      if (!syncStatus.externalContactId) {
        return ''
      }
      const baseUrl = syncStatus.baseUrl || 'https://wealth.emaplan.com'
      return `${baseUrl}/ema/Advisor/Clients/${syncStatus.externalContactId}/Overview`
    }
    //return `${syncStatus.baseUrl}/main.aspx?etn=${syncStatus.externalContactObjectType}&id=${syncStatus.contactId}`
    case 'advisorengine':
      switch (crmObjectName) {
        case 'contact':
          return `https://crm-x.advisorengine.com/Record/Record/RecordDetails?RecordID=${syncStatus.externalContactId}`
      }
      return ''
  }
  return ''
}

export const getContactChangeSyncStatusViews = (contactChanges: ContactChange[], users: User[]): SyncStatusView[] => {
  const userById = new Map(users.map((u) => [u.id, u]))

  return contactChanges.flatMap(
    ({ externalId, userId, id: contactChangeId, syncStatus }) =>
      syncStatus?.map(({ metadata, ...rest }) => ({
        ...rest,
        type: externalId === 'new' ? 'createContact' : 'updateContact',
        name: getUserDisplayName(userById.get(userId), 'Contact') ?? 'Contact',
        metadata: { ...metadata, contactChangeId },
      })) ?? [],
  )
}
